import { sendToApi } from 'api/client'
import { PaginatedApiClient } from 'api/PaginatedApiClient'
import { DateFormat } from 'common/PropertyForm/Types/Date'
import { NumberFormat } from 'common/PropertyForm/Types/Number'
import qs from 'qs'
import { MaybeTransitionEffectPayload } from 'utils/workflows/runAfterTransitionEffects'

export interface NameProperty extends Omit<Property, 'id'> {
  id: null
  default_type: DefaultType.Name
  name: 'Name'
}

export interface Property<T extends Omit<PropertyValue, 'id'> = PropertyValue> {
  id: number
  is_default: boolean
  is_editable?: boolean
  type: PropertyType
  default_type: DefaultType | null
  property_tag: PropertyTag | null
  name: string
  updated_at: string
  property_values: T[]
  default_for_articles?: boolean
  scope_project_id?: string | null
  workflow_project_id?: string | null
  meta?: {
    number_format?: NumberFormat
    allow_mentioning_multiple_people?: boolean
    notify_person?: boolean
    date_format?: DateFormat
  }
  workspace_id: number
  position?: number
}

export interface DocumentPropertyValue extends PropertyValue {
  value: {
    title: string
    header_emoji: string
    key: string
  }
}

export interface PersonPropertyValue extends PropertyValue {
  value: {
    creator_id?: number
    creator_name: string
    creator_image_url: string
  }
}

export interface CampaignPropertyValue extends Omit<PropertyValue, 'id'> {
  id: number
  value: {
    name: string
  }
}

export interface SelectPropertyValue extends Omit<PropertyValue, 'id'> {
  id: number
  position: number
  meta: {
    color: string
    description?: string
  }
  connected_to_id: number | null
}

export interface PropertyValue {
  id: number | string
  value: any
  property_id: number
  position?: number
  meta?: {
    color?: string
  }
  value_is_disabled?: boolean
}

export interface AssignedProperty {
  id?: number
  property: Property
  property_values: PropertyValue[]
  assignee_id: string
  assignee_type: AssigneeType
}

export interface VirtualAssignedProperty extends Omit<AssignedProperty, 'assignee_type' | 'assignee_id'> {}

export interface AssignedPropertyValue {
  assigned_value_id: number
  assigned_value_type: AssignedValueType
}

export enum AssignedValueType {
  Article = 'Article',
  PropertyValue = 'PropertyValue',
  User = 'User',
}

export enum PropertyType {
  Title = 'title', // Special case for name attribute
  Text = 'text',
  SingleSelect = 'single_select',
  MultiSelect = 'multi_select',
  Date = 'date',
  Document = 'document',
  Person = 'person',
  Checkbox = 'checkbox',
  Number = 'number',
  Link = 'link',
  Campaign = 'campaign',
}

export enum DefaultType {
  Name = 'name', // Special case for name attribute
  Owner = 'owner',
  Created = 'created',
  Updated = 'updated',
  WordCount = 'wordcount',
  ItemsCount = 'items_count',
  Status = 'status',
  Assignee = 'assignee',
  LastOpened = 'last_opened',
  LastUpdatedByMe = 'last_updated_by_me',
  CreatedByMe = 'created_by_me',
  Shared = 'shared',
  Posts = 'posts',
  PostStatus = 'post_status',
}

export enum PropertyTag {
  CampaignName = 'campaign_name',
  Channel = 'channel',
  LaunchDate = 'launch_date',
  PostAt = 'post_at',
}

export const AttributeProperties: { [key: string]: Property } = {
  name: {
    type: PropertyType.Title,
    name: 'Name',
    default_type: DefaultType.Name,
  } as Property,
}

export const attributePropertyTypes = [PropertyType.Title]

export const PropertyLabelMap: { [key: string]: string } = {
  default: 'Default',
  link: 'Link',
  number: 'Number',
  person: 'Person',
  date: 'Date',
  document: 'Document',
  multi_select: 'Multi Select',
  single_select: 'Single Select',
  text: 'Text',
  checkbox: 'Checkbox',
}

export enum AssigneeType {
  Article = 'Article',
  Collection = 'Collection',
  Project = 'Project',
  MediaFile = 'MediaFile',
  Design = 'Design',
  Video = 'Video',
}

export enum PostStatusPropertyValue {
  AutoPostScheduled = 'Autoscheduled',
  Draft = 'Draft',
  Posted = 'Posted',
  ReminderScheduled = 'Reminder Set',
  Failed = 'Failed',
}

export const PropertiesForForms = [
  PropertyType.SingleSelect,
  PropertyType.MultiSelect,
  PropertyType.Document,
  PropertyType.Person,
  PropertyType.Text,
  PropertyType.Checkbox,
  PropertyType.Date,
  PropertyType.Number,
  PropertyType.Link,
]

export const instanceOfDocumentValue = (propertyValue: PropertyValue): propertyValue is DocumentPropertyValue => {
  return propertyValue.value instanceof Object && 'title' in propertyValue.value
}

export const instanceOfPersonValue = (propertyValue: PropertyValue): propertyValue is PersonPropertyValue => {
  return propertyValue.value instanceof Object && 'creator_name' in propertyValue.value
}

export interface Assignee {
  id: string
  type: AssigneeType
}

export const PROPERTY_TYPES_FOR_SHORTCUT = [
  DefaultType.Name,
  DefaultType.Owner,
  DefaultType.Created,
  DefaultType.Updated,
  DefaultType.Status,
]

export const PropertyClient = {
  getProperties: (workspaceKey: string, queryParams: { [key: string]: any } = {}) => {
    const searchParams = new URLSearchParams()

    for (const key in queryParams) {
      searchParams.set(key, queryParams[key])
    }

    return sendToApi<{ properties: Property[] }>(`/w/${workspaceKey}/properties?${searchParams.toString()}`)
  },
  createProperty: (workspaceKey: string, property: Property) => {
    return sendToApi<{ property: Property }>(`/w/${workspaceKey}/properties`, {
      method: 'POST',
      body: {
        property,
      },
    })
  },
  updateProperty: (
    workspaceKey: string,
    property: Property,
    articleId?: string,
    isUndoAction?: boolean,
    position?: number,
  ) => {
    return sendToApi<{ property: Property }>(`/w/${workspaceKey}/properties/${property.id}`, {
      method: 'PUT',
      body: {
        article_id: articleId,
        property,
        position,
        is_undo_action: isUndoAction,
      },
    })
  },
  deleteProperty: (workspaceKey: string, property: Property) => {
    return sendToApi<{ property: Property }>(`/w/${workspaceKey}/properties/${property.id}`, {
      method: 'DELETE',
    })
  },
  createPropertyValue: (workspaceKey: string, propertyValue: PropertyValue) => {
    return sendToApi<{ property_value: PropertyValue }>(`/w/${workspaceKey}/property_values`, {
      method: 'POST',
      body: {
        property_value: propertyValue,
      },
    })
  },
  updatePropertyValue: (workspaceKey: string, propertyValue: PropertyValue) => {
    return sendToApi<{ property_value: PropertyValue }>(`/w/${workspaceKey}/property_values/${propertyValue.id}`, {
      method: 'PUT',
      body: {
        property_value: propertyValue,
      },
    })
  },
  deletePropertyValue: (workspaceKey: string, propertyValue: PropertyValue) => {
    return sendToApi<{ property_value: PropertyValue }>(`/w/${workspaceKey}/property_values/${propertyValue.id}`, {
      method: 'DELETE',
    })
  },
  createAssignedProperty: <AssignedProperty>(
    assigneeId: string | number,
    property: Property,
    assigneeType = AssigneeType.Article,
  ) => {
    return sendToApi<{ assigned_property: AssignedProperty }>(`/assigned_properties`, {
      method: 'POST',
      body: { assignee_id: assigneeId, property, assignee_type: assigneeType },
    })
  },
  findOrCreateAssignedProperty: <AssignedProperty>(
    assigneeId: string | number,
    property: Property,
    assigneeType = AssigneeType.Article,
  ) => {
    return sendToApi<{ assigned_property: AssignedProperty }>(`/assigned_properties/find_or_create`, {
      method: 'POST',
      body: { assignee_id: assigneeId, property, assignee_type: assigneeType },
    })
  },
  updateAssignedProperty: <AssignedProperty>(
    id: number,
    property: Property,
    assignedPropertyValues: AssignedPropertyValue[],
    payload?: MaybeTransitionEffectPayload,
  ) => {
    return sendToApi<{ assigned_property: AssignedProperty }>(`/assigned_properties/${id}`, {
      method: 'PUT',
      body: {
        property_id: property.id,
        assigned_property_values: assignedPropertyValues.map((apv) => ({
          ...apv,
          assigned_value_id: String(apv.assigned_value_id),
        })),
        payload,
      },
    })
  },
  updatePropertyAssignedProperty: (
    property_id: number,
    assignee: Assignee,
    assignedPropertyValues: AssignedPropertyValue[],
    payload?: MaybeTransitionEffectPayload,
    on_create?: boolean,
  ) => {
    return sendToApi<{ assigned_property: AssignedProperty }>(
      `/properties/${property_id}/assigned_properties?assignee_type=${assignee.type}&assignee_id=${assignee.id}`,
      {
        method: 'PUT',
        body: {
          assigned_property_values: assignedPropertyValues.map((apv) => ({
            ...apv,
            assigned_value_id: String(apv.assigned_value_id),
          })),
          payload,
          on_create,
        },
      },
    )
  },
  getAssignedProperties: <AssignedProperty>(
    assigneeId: string,
    assigneeType = AssigneeType.Article,
    propertyId?: number,
  ) => {
    const params = qs.stringify({
      property_id: propertyId,
      assignee_id: assigneeId,
      assignee_type: assigneeType,
    })
    return new PaginatedApiClient<{ assigned_properties: AssignedProperty[] }>(`/assigned_properties?${params}`)
  },
  getAssignedProperty: <AssignedProperty>(id: number) => {
    return sendToApi<{ assigned_property: AssignedProperty }>(`/assigned_properties/${id}`)
  },
  getBatchAssignedPropertyValues: (assignees: Assignee[]) => {
    return new PaginatedApiClient<{ assigned_properties: AssignedProperty[] }>(
      `/assigned_property_values/batch_index`,
      {
        method: 'POST',
        body: { assignees },
      },
    )
  },
  getBatchAssignedProperties: (assignees: Assignee[], propertiesIds: number[]) => {
    return sendToApi<{ assigned_properties: AssignedProperty[] }>(`/assigned_properties/batch_index`, {
      method: 'POST',
      body: { assignees, properties_ids: propertiesIds },
    })
  },
  bulkUpdateAssignedProperties: (
    assignees: Assignee[],
    assignedPropertyValues: AssignedPropertyValue[],
    propertyId: number,
  ) => {
    return sendToApi<{
      assigned_properties: AssignedProperty[]
    }>(`/assigned_properties`, {
      method: 'PUT',
      body: {
        assignees,
        assigned_property_values: assignedPropertyValues,
        property_id: propertyId,
      },
    })
  },
  deleteAssignedProperty: (id: number) => {
    return sendToApi(`/assigned_properties/${id}`, {
      method: 'DELETE',
    })
  },

  bulkDeleteAssignedProperties: (assignees: Assignee[], propertyId: number, options = {}) => {
    return sendToApi<{}>(`/assigned_properties`, {
      method: 'DELETE',
      body: {
        assignees,
        property_id: propertyId,
        ...options,
      },
    })
  },
  createAssignedPropertyValue: (assignedPropertyId: number, propertyValue: PropertyValue) => {
    return sendToApi<{ assigned_property: AssignedProperty }>(`/assigned_properties/${assignedPropertyId}/values`, {
      method: 'POST',
      body: {
        assigned_property_id: assignedPropertyId,
        property_value_id: propertyValue.id,
      },
    })
  },
}
